import React, { useEffect, useState } from 'react'
import {
  Form,
  Popconfirm,
  Table,
  TableProps,
  Typography,
  Modal,
  Input,
} from 'antd'
import { RevisionEditableCell } from '../RevisionEditableCell'
import {
  $completedRevisionData,
  $createdRevisionData,
  $updatedRevisionData,
  $correctionSaveData,
  completeRevisionMounted,
  revisionsHistoryMounted,
  updateRevisionMounted,
  sendCorrectionRequest,
} from '../../details/model'
import { useStore } from 'effector-react'
import { RevisionItemDetailFromServer } from '../../details/types'
// @ts-ignore
import styles from './styles.module.css'

type Props = {
  data: RevisionItemDetailFromServer[]
  totalItems: number
  currency: string
  walletId: string
}

export const RevisionHistoryTable: React.FC<Props> = ({
  data,
  totalItems,
  currency,
  walletId,
}) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const updatedData = useStore($updatedRevisionData)
  const completedData = useStore($completedRevisionData)
  const createdData = useStore($createdRevisionData)
  const correctionData = useStore($correctionSaveData)

  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)
  const [comment, setComment] = useState('')
  const [currentItem, setCurrentItem] =
    useState<RevisionItemDetailFromServer | null>(null)
  const [correctionModalVisible, setCorrectionModalVisible] = useState(false)
  const [correctionComment, setCorrectionComment] = useState('')

  const [editingKey, setEditingKey] = useState<number | null>()

  useEffect(() => {
    if (currency && walletId) {
      revisionsHistoryMounted({
        limit: pageSize,
        page,
        currency,
        walletId,
      })
    }
  }, [
    page,
    pageSize,
    updatedData,
    currency,
    walletId,
    completedData,
    createdData,
    correctionData,
  ])

  useEffect(() => {
    if (completedData.result && currentItem) {
      setModalVisible(true)
    } else {
    }
  }, [completedData, currentItem])

  const isEditing = (record: RevisionItemDetailFromServer) =>
    record.id === editingKey

  const edit = (record: RevisionItemDetailFromServer) => {
    form.setFieldsValue({ ...record })
    setEditingKey(record.id)
  }

  const save = (item: RevisionItemDetailFromServer) => {
    updateRevisionMounted({
      id: item.id,
      psBalance: Number(form.getFieldValue('psBalance')),
    })
    setEditingKey(null)
  }

  const complete = (item: RevisionItemDetailFromServer) => {
    setCurrentItem(item)
    completeRevisionMounted({
      id: item.id,
    })
  }

  const handleOk = () => {
    if (currentItem) {
      sendCorrectionRequest({
        revision: currentItem.id,
        comment,
        discrepancy: currentItem.discrepancy,
        discrepancyUsd: currentItem.discrepancyUSD,
      })
      setModalVisible(false)
      setComment('')
      setEditingKey(null)
      completedData.result = false
    }
  }

  const handleCancel = () => {
    setModalVisible(false)
    setComment('')
    setEditingKey(null)
  }

  const handleCorrectionClick = (comment: string | undefined) => {
    if (comment !== 'undefined') {
      setCorrectionComment(comment as string)
      setCorrectionModalVisible(true)
    }
  }

  const handleCorrectionModalOk = () => {
    setCorrectionModalVisible(false)
    setCorrectionComment('')
  }

  const cancel = () => {
    setEditingKey(null)
  }

  const handleTableChange = ({
    currentPage,
    currentPageSize,
  }: {
    currentPage?: number
    currentPageSize?: number
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
  }

  // Функция для поиска последней записи со статусом completed
  function getLastCompletedDate(
    records: RevisionItemDetailFromServer[],
  ): Date | null {
    const lastCompletedRecord = records
      .filter((record) => record.status === 'COMPLETED')
      .sort(
        (a, b) =>
          new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime(),
      )[0] // Сортировка по completedAt и выбор последней записи

    return lastCompletedRecord
      ? new Date(lastCompletedRecord.completedAt)
      : null
  }

  // Используем найденную дату
  const lastCompletedDate = getLastCompletedDate(data)

  // Количество дней с момента предыдущей сверки
  const daysSinceLastCheck = lastCompletedDate
    ? Math.max(
        1,
        Math.floor(
          (new Date().getTime() - lastCompletedDate.getTime()) /
            (1000 * 60 * 60 * 24),
        ), // разница в днях
      )
    : 1 // Если записи нет, устанавливаем значение 1

  // @ts-ignore
  const columns: ColumnType<RevisionItem>[] = [
    {
      title: 'Date and time',
      editable: false,
      render: (item: RevisionItemDetailFromServer) => item.humanCreatedAt,
    },
    {
      title: 'Completed at',
      editable: false,
      render: (item: RevisionItemDetailFromServer) => item.humanCompletedAt,
    },
    {
      title: 'Balance CRM',
      editable: false,
      render: (item: RevisionItemDetailFromServer) =>
        `${item.crmBalance} ${item.currency}`,
    },
    {
      title: 'Balance PS',
      editable: true,
      dataIndex: 'psBalance',
      render: (psBalance: number) => `${psBalance}`,
    },
    {
      title: 'Discrepancy',
      editable: false,
      render: (item: RevisionItemDetailFromServer) =>
        `${item.discrepancy > 0 ? '+' : ''}${item.discrepancy} ${
          item.currency
        }`,
    },
    {
      title: 'Author',
      editable: false,
      render: (item: RevisionItemDetailFromServer) => item.createdBy.email,
    },
    {
      title: 'Correction',
      editable: false,
      render: (item: RevisionItemDetailFromServer) =>
        item.correction ? (
          <Typography.Link
            onClick={() => handleCorrectionClick(item?.correction?.comment)}
          >
            {item.correction.discrepancy} {item.currency}
          </Typography.Link>
        ) : (
          'No correction'
        ),
    },
    {
      title: 'Action',
      render: (valueRow: RevisionItemDetailFromServer) => {
        const editable = isEditing(valueRow)

        if (valueRow.status === 'COMPLETED') {
          return null
        }

        // Определяем, можно ли завершить
        const canComplete =
          valueRow.discrepancy === 0 ||
          (valueRow.discrepancyUSD !== null &&
            Math.abs(valueRow.discrepancyUSD) < daysSinceLastCheck)

        return (
          <>
            {/* Кнопка Complete, если можно завершить */}
            {canComplete && (
              <>
                <Typography.Link onClick={() => complete(valueRow)}>
                  Complete
                </Typography.Link>{' '}
              </>
            )}
            {/* Кнопка Save и Cancel, если редактируется */}
            {editable ? (
              <span>
                <Typography.Link
                  onClick={() => save(valueRow)}
                  style={{ marginRight: 8 }}
                >
                  Save
                </Typography.Link>
                <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                  <a>Cancel</a>
                </Popconfirm>
              </span>
            ) : (
              // Кнопка Edit, если не редактируется
              <Typography.Link
                disabled={Boolean(editingKey)}
                onClick={() => edit(valueRow)}
              >
                Edit
              </Typography.Link>
            )}
          </>
        )
      },
    },
  ]

  const mergedColumns: TableProps['columns'] = columns.map((col) => {
    if (!col.editable) {
      return col
    }

    return {
      ...col,
      onCell: (record: RevisionItemDetailFromServer) => ({
        record,
        inputType: 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    }
  })

  return (
    <>
      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: RevisionEditableCell,
            },
          }}
          rowClassName={(item) =>
            item.discrepancy !== 0 && item.status !== 'COMPLETED'
              ? styles.row
              : ''
          }
          bordered
          rowKey="id"
          scroll={{ x: true }}
          dataSource={data}
          columns={mergedColumns}
          pagination={{
            onChange: cancel,
            showSizeChanger: true,
            total: totalItems,
          }}
          onChange={(pagination) =>
            handleTableChange({
              //@ts-ignore
              currentPage: pagination.current, //@ts-ignore
              currentPageSize: pagination.pageSize, //@ts-ignore
            })
          }
        />
      </Form>
      <Modal
        title="Add Comment"
        open={modalVisible}
        onOk={handleOk}
        closable={false}
        cancelButtonProps={{ disabled: true }}
      >
        <Input.TextArea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Enter your comment"
        />
      </Modal>
      <Modal
        title="Correction Comment"
        open={correctionModalVisible}
        footer={null}
        onCancel={handleCorrectionModalOk}
      >
        <p>{correctionComment}</p>
      </Modal>
    </>
  )
}
